import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import SliderFieldUI from '@axo/form/feature/form-input/SliderField/SliderFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { getLoanCost } from '@axo/shared/util/loan';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Separator } from '@axo/ui-core/components/Separator';
import { HighCostInformationBanner } from '@axo/ui-feature/components/HighCostInformationBanner';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from '../../../../../components/Form';
import { Navigation } from '../../../../../components/Navigation';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import useInitiatedTracking from '../../../../../hooks/tracking/useInitiatedTracking';
import { getMarketingConsentDefaultValues } from '../../../../../hooks/useMarketingConsent';
import { formatSpacedNumber } from '../../../../../utils/format';
import {
  DEFAULT_LOAN_AMOUNT,
  DEFAULT_LOAN_DURATION,
  INTEREST_RATE,
  MAX_LOAN_AMOUNT,
  MAX_LOAN_DURATION,
  MIN_LOAN_AMOUNT,
  MONTHLY_FEE,
  STARTUP_FEE,
} from '../../config/globals';
import { Step1Data } from '../../FormData.se.types';
import { Information } from '../../Information';
import styles from '../../loan.module.scss';
import { PurposeFormGroup } from '../shared/PurposeFormGroup';
import { appliedAmountFieldOptions } from './config';
import { CustomerFormGroup } from './CustomerFormGroup';

const touchedFieldsOverride: Partial<keyof Step1Data>[] = [
  'LoanApplication/AppliedAmount',
  'LoanApplication/LoanDuration',
  'LoanApplication/Purpose',
  'Debt/Unspecified/Type',
  'Debt/Unspecified/MonthlyDebtPayment',
  'Debt/Unspecified/RefinanceAmount',
];

const getDefaultValues = ({
  loanAmount,
  loanDuration,
  marketingConsent,
  showMarketingConsent,
}: {
  loanAmount?: number;
  loanDuration?: number;
  marketingConsent?: boolean;
  showMarketingConsent?: boolean;
}): Step1Data => ({
  'LoanApplication/InitialPurpose': null,
  'LoanApplication/AppliedAmount': loanAmount || DEFAULT_LOAN_AMOUNT,
  'LoanApplication/LoanDuration': loanDuration || DEFAULT_LOAN_DURATION,
  'Customer/Email': '',
  'Customer/MobilePhoneNumber': '',
  ...getMarketingConsentDefaultValues({
    marketingConsent,
    showMarketingConsent,
    defaultValueOverride: false,
  }),
});

interface Props {
  isPre?: boolean;
  heading?: string;
  options?: {
    defaultLoanAmount?: number;
    defaultLoanDuration?: number;
  };
}

export const Step1 = ({ isPre, options }: Props) => {
  const { t } = useTranslation();

  const { onChange, onSubmit, onPreSubmit, axoFormData } =
    useAxoFormContext<Step1Data>();

  const [isEmailValid, setIsEmailValid] = useState(false);

  const form = useForm({
    defaultValues: getDefaultValues({
      loanAmount:
        axoFormData['LoanApplication/AppliedAmount'] ??
        options?.defaultLoanAmount,
      loanDuration:
        axoFormData['LoanApplication/LoanDuration'] ??
        options?.defaultLoanDuration,
      marketingConsent: axoFormData['Ignore/MarketingConsent'],
      showMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
    }),
    mode: 'onChange',
  });

  const {
    watch,
    setError,
    formState: { isSubmitting, isSubmitSuccessful, touchedFields },
  } = form;

  useInitiatedTracking({
    shouldTriggerOnInitiated: Object.keys(touchedFields).length > 0,
  });

  const loading = isSubmitting || isSubmitSuccessful;

  const showMoreFields =
    !!watch('LoanApplication/InitialPurpose') || axoFormData['Customer/Email'];

  const appliedAmount = watch('LoanApplication/AppliedAmount') ?? 0;
  const loanDuration = watch('LoanApplication/LoanDuration') ?? 0;

  const { monthlyPayment } = useMemo(
    () =>
      getLoanCost(
        appliedAmount,
        loanDuration,
        INTEREST_RATE,
        MONTHLY_FEE,
        STARTUP_FEE
      ),
    [appliedAmount, loanDuration]
  );

  const durationRightLabel = useMemo(
    () => ({
      label: t('LoanDuration.rightLabel.label'),
      value: t('LoanDuration.rightLabel.value', { monthlyPayment }),
    }),
    [monthlyPayment, t]
  );

  const _onSubmit = async (data: Step1Data) => {
    if (!isEmailValid) {
      setError('Customer/Email', { message: 'Email.error.pattern' });
      return;
    }

    if (isPre) {
      onPreSubmit(data);
      return;
    }

    onSubmit(data);
  };

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={_onSubmit}
        onChange={onChange}
        /* We need to override the touched attribute for this for it to trigger a change immediately when it is interacted with.
         Otherwise, the user would need to interact with another field before the change is triggered.*/
        touchedFieldsOverride={touchedFieldsOverride}
      >
        <Stack gap={'xs'}>
          <span className={styles.appliedAmount}>
            <NumberFieldUI<Step1Data>
              name="LoanApplication/AppliedAmount"
              label={t('AppliedAmount.label')}
              data-testid="applied-amount-input"
              autoSetValues
              size="large"
              suffix={t('suffix.currency')}
              format={formatSpacedNumber}
              disableSuccessState={true}
              {...appliedAmountFieldOptions}
            />
          </span>

          <SliderFieldUI<Step1Data>
            name="LoanApplication/AppliedAmount"
            data-testid="loan-amount-slider"
            valueSuffix={t('suffix.currency')}
            min={MIN_LOAN_AMOUNT}
            max={MAX_LOAN_AMOUNT}
            step={5_000}
          />

          <div data-testid="loan-duration-slider">
            <SliderFieldUI<Step1Data>
              name="LoanApplication/LoanDuration"
              data-testid="loan-duration-slider"
              valueSuffix={t('suffix.year')}
              label={t('LoanDuration.label')}
              rightLabel={durationRightLabel}
              min={1}
              max={MAX_LOAN_DURATION}
              step={1}
            />
          </div>
        </Stack>

        <Separator className={styles.separator} color={'light'} />

        <PurposeFormGroup />

        {showMoreFields && (
          <CustomerFormGroup
            isEmailValid={isEmailValid}
            setIsEmailValid={setIsEmailValid}
          />
        )}

        <Navigation.Continue loading={loading} size={isPre ? 'xl' : 'l'}>
          {t('continue')}
        </Navigation.Continue>
      </Form>
      {isPre && <Information />}
      <HighCostInformationBanner />
    </FormProvider>
  );
};
